import { API } from '../constants/api';

import { ITrackingNotificationRecipient } from './CustomerService';
import { axiosInterceptor } from './interceptors';

// Custom view CRUD for orders
export const getOrderViewList = async (params: any) => {
    const response = await axiosInterceptor.get(API.VIEW.LIST, { params });

    return response;
};

export const createOrderView = async (data: any) => {
    const response = await axiosInterceptor.post(API.VIEW.CREATE, data);

    return response;
};

export const updateOrderView = async (id: string, data: any) => {
    const response = await axiosInterceptor.patch(`${API.VIEW.UPDATE}/${id}`, data);

    return response;
};

export const deleteOrderView = async (id: number) => {
    const response = await axiosInterceptor.delete(`${API.VIEW.DELETE}/${id}`);

    return response;
};

// Table column settings for orders
export const updateOrderTableColumnSettings = async (data: any) => {
    const response = await axiosInterceptor.patch(API.TABLE_COLUMN_SETTINGS.UPDATE, data);

    return response;
};

export const getOrderTableColumnSettings = async (params: any) => {
    const response = await axiosInterceptor.get(API.TABLE_COLUMN_SETTINGS.GET, { params });

    return response;
};

export const getOrdersSummary = async (data: {
    tab: 'all' | 'pickup' | 'delivery';
    page?: number;
    limit?: number;
    startDate?: string;
    endDate?: string;
    shipperId?: number | null;
    consigneeId?: number | null;
}) => {
    const response = await axiosInterceptor.post(API.ORDERS.SUMMARY, data);

    return response;
};

export const orderList = async (params: any, signal?: any) => {
    const response = await axiosInterceptor.get(API.ORDERS.LIST, {
        params,
        signal,
    });

    return response;
};

export const orderListCount = async (params: any, signal?: any) => {
    const response = await axiosInterceptor.get(API.ORDERS.LIST_COUNT, {
        params,
        signal,
    });

    return response;
};

export const getOrders = async (params: any) => {
    const response = await axiosInterceptor.get(`${API.ORDERS.GET_ORDERS}`, {
        params,
    });

    return response;
};

export const getActiveOrder = async (params: any) => {
    const response = await axiosInterceptor.post(`${API.ORDERS.GET_ACTIVE_ORDER}`, params);

    return response;
};

/**
 * `context: 'tracking'` tells the backend this fetch backs the Track detail page (which also loads the
 * radar endpoint), so it records a tracking view only, not an order view as well.
 */
export const getOrderById = async (id: string, options?: { context?: 'tracking' }) => {
    const response = await axiosInterceptor.get(`${API.ORDERS.GET_ORDER_BY_ID}/${id}`, options?.context ? { params: { context: options.context } } : undefined);

    return response;
};

export const getToOrderDetails = async (orderId: string) => {
    const response = await axiosInterceptor.get(`${API.ORDERS.ORDER_FLOW_V2.GET_ORDER_DETAILS}/${orderId}`);

    return response;
};

export const updateOrderPriority = async (id: string, isPriority: boolean) => {
    console.log('🚀 ~ updateOrderPriority ~ id:', id);
    const response = await axiosInterceptor.patch(`${API.ORDERS.UPDATE_PRIORITY}/${id}`, { isPriority });

    return response;
};

export interface IOrderShareLinkResponse {
    orderStatus: string | null;
    /** 'order-share' token — portal order page (/share-links/order?t=) */
    orderToken: string;
    /** 'radar-share' token — live tracking page (/share-links/radar-order?t=) */
    radarToken: string;
}

export const getOrderShareLink = async (id: string) => {
    const response = await axiosInterceptor.get(`${API.ORDERS.GET_ORDER_BY_ID}/${id}${API.ORDERS.SHARE_LINK_SUFFIX}`);

    return response;
};

// ── Per-order tracking notification recipients (order_metadata.trackingNotificationRecipients) ──
export interface IOrderTrackingNotificationRecipient {
    id: string;
    type: 'email' | 'phone';
    value: string;
    addedAt: string;
}

/** Returns { recipients: on this order, availableRecipients: customer pool } in one call. */
export const getOrderTrackingRecipients = async (id: string) => {
    const response = await axiosInterceptor.get(`${API.ORDERS.GET_ORDER_BY_ID}/${id}${API.ORDERS.TRACKING_RECIPIENTS_SUFFIX}`);

    return response;
};

export const addOrderTrackingRecipients = async (id: string, recipientIds: string[]) => {
    const response = await axiosInterceptor.post(`${API.ORDERS.GET_ORDER_BY_ID}/${id}${API.ORDERS.TRACKING_RECIPIENTS_SUFFIX}`, { recipientIds });

    return response;
};

/** Per-recipient outcome of the "send the link to whoever is new" step of a save. */
interface ITrackingNotificationResult {
    id: string;
    type: 'email' | 'phone';
    value: string;
    sent: boolean;
    reason?: string;
}

export interface ISaveOrderTrackingRecipientsResponse {
    recipients: IOrderTrackingNotificationRecipient[];
    /** Customer-wide pool after the save — new values typed here are appended to it. */
    pool: ITrackingNotificationRecipient[];
    addedCount: number;
    removedCount: number;
    /** False when the order's status has no shareable page yet: recipients saved, nothing sent. */
    linkAvailable: boolean;
    notified: { sent: number; failed: number; results: ITrackingNotificationResult[] };
}

/**
 * Replace the order's recipient list in one call and send the tracking link to whoever is NEW.
 *
 * `recipients` is the complete desired list of raw values (emails / phone numbers), not a delta —
 * the modal stages adds and removals locally and commits them with one "Save & send link" press.
 * Recipients already on the order are never re-notified.
 */
export const saveOrderTrackingRecipients = async (id: string, recipients: string[]) => {
    const response = await axiosInterceptor.put(`${API.ORDERS.GET_ORDER_BY_ID}/${id}${API.ORDERS.TRACKING_RECIPIENTS_SUFFIX}`, { recipients });

    return response;
};

export const removeOrderTrackingRecipient = async (id: string, recipientId: string) => {
    const response = await axiosInterceptor.delete(`${API.ORDERS.GET_ORDER_BY_ID}/${id}${API.ORDERS.TRACKING_RECIPIENTS_SUFFIX}/${recipientId}`);

    return response;
};

export const saveOrderNotificationSettings = async (
    id: string,
    notificationTypes: import('./CustomerService').INotificationTypes,
    // Per-load Send to (cp-meeting-changes F2); omitted by callers without the block.
    delivery?: { deliveryEmail: string | null; deliveryPhone: string | null }
) => {
    const response = await axiosInterceptor.post(`${API.ORDERS.ORDER_NOTIFICATION_SETTINGS}/${id}`, { notificationTypes, ...(delivery ?? {}) });

    return response;
};

export const cancelOrder = async (id: string, cancellationReason?: string) => {
    const response = await axiosInterceptor.post(`${API.ORDERS.CUSTOMER_REQUEST}/${id}`, {
        requestType: 'cancellation',
        description: cancellationReason || null,
    });

    return response;
};

export const updateSpecialInstructions = async (orderLegId: number, specialInstructions: string) => {
    const response = await axiosInterceptor.patch(`${API.ORDERS.SPECIAL_INSTRUCTIONS}/${orderLegId}`, {
        specialInstructions,
    });

    return response;
};

export const updateOrderLegNotes = async (orderLegId: number, noteType: 'pickup' | 'delivery', notes: string) => {
    const response = await axiosInterceptor.patch(`${API.ORDERS.LEG_NOTES}/${orderLegId}`, { noteType, notes });

    return response;
};

export const deleteLegDocument = async (documentId: number) => {
    const response = await axiosInterceptor.delete(`${API.ORDERS.LEG_DELETE_DOCUMENT}/${documentId}`);

    return response;
};

export const uploadLegDocument = async (orderLegId: string | number, formData: FormData) => {
    const response = await axiosInterceptor.post(`${API.ORDERS.LEG_UPLOAD_DOCUMENT}/${orderLegId}`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
    });

    return response;
};

export const requestUpdateLocation = async (orderId: string) => {
    const response = await axiosInterceptor.post(`${API.ORDERS.REQUEST_UPDATE_LOCATION}/${orderId}`);

    return response;
};

export const createEditRequest = async (orderId: string, editType: string, requestData: object, description?: string, orderLegId?: number) => {
    const response = await axiosInterceptor.post(`${API.ORDERS.CUSTOMER_REQUEST}/${orderId}`, {
        requestType: 'edit',
        editType,
        requestData,
        description: description || null,
        // Leg the edit targets (multi-leg orders only) — omitted/null keeps the
        // request order-level, matching single-leg behavior
        orderLegId: orderLegId ?? null,
    });

    return response;
};
